import { _ } from "../../core/polyglot/polyglot.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import AnnualTrend from "../../models/accounting/AnnualTrend.js";
import Budget from "../../models/Budget.js";
import BusinessTrendActionsWidget from "./BusinessTrendActionsWidget.js";
import BusinessTrendComparisonData from "./BusinessTrendComparisonData.js";
import BusinessTrendDocumentService from "./BusinessTrendDocumentService.js";
import BusinessTrendWidget from "./BusinessTrendWidget.js";
import FiscalYearBusinessTrendComparisonElement from "./FiscalYearBusinessTrendComparisonElement.js";
import session from "../../core/session.js";

/**
 * Accounting document letting the user compare the business results
 * of the current year to another.
 */
const businessTrendDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		that.showUpdating();

		that.title = _("Business trend");

		that.documentId = "business-trend";
		const antiForgeryToken = session.getAntiForgeryToken();

		my.fiscalYearAnnualTrendRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/FiscalYear/#fiscalYearId/AnnualTrend",
			defaultFilters: { companyId: session.getCompany().getLegacyId() },
			model: AnnualTrend,
			antiForgeryToken,
		});

		my.budgetRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/Budget",
			defaultFilters: { companyId: session.getCompany().getLegacyId() },
			model: Budget,
			antiForgeryToken,
		});

		my.budgetAnnualTrendRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/Budget/#budgetId/AnnualTrend",
			defaultFilters: { companyId: session.getCompany().getLegacyId() },
			model: AnnualTrend,
			antiForgeryToken,
		});

		// An instance of BusinessTrendComparisonData representing the
		// current period:
		my.primaryComparisonData = undefined;

		// An instance of BusinessTrendComparisonData representing the
		// data selected by the dropdown in the action bar:
		my.secondaryComparisonData = undefined;

		my.businessTrendDocumentService = new BusinessTrendDocumentService({
			session,
		});
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.businessTrend = new BusinessTrendWidget();

		my.businessTrendActionsWidget = new BusinessTrendActionsWidget({
			onChange: () => {
				my.setSecondaryComparisonData();
				my.updateComparisonData();
			},
		});
	};

	that.help = function () {
		return [
			{
				title: _("Business trend"),
				content: _(
					"The yellow bars visualize monthly profits and losses and the green line represents the accumulated result for the fiscal year.",
				),
				element: "business-chart-container",
			},
			{
				title: _("Comparing"),
				content: _(
					"You can compare to previous year or budget by clicking on the legends below the chart.",
				),
				element: "legend-container",
			},
			{
				title: _("Compare to"),
				content: _(
					"You can select if you want to compare to previous fiscal years or budget in the drop down list.",
				),
				element: "compare-to",
			},
		];
	};

	that.subtitle = function (html) {
		if (!my.comparisonStateIsValid()) {
			return;
		}

		html.render(
			_("Comparing {period1} to {period2} for {company}", {
				company: session.getCompany().getName(),
				period1: my.comparisonDataNameRenderer(
					my.primaryComparisonData,
				),
				period2: my.comparisonDataNameRenderer(
					my.secondaryComparisonData,
				),
			}),
		);
	};

	that.periodChanged = function () {
		if (!my.comparisonStateIsValid()) {
			return;
		}

		my.setPrimaryComparisonData();

		my.businessTrendActionsWidget.setCurrentFiscalYear(
			session.getPeriod().getYear(),
		);

		my.setSecondaryComparisonData();
		my.updateComparisonData();
	};

	that.actions = function () {
		return [my.businessTrendActionsWidget];
	};

	that.show = async function ({ isForecastingEnabled }) {
		my.hideActions();
		that.showUpdating();

		my.setPrimaryComparisonData();

		my.businessTrendActionsWidget.setComparisonElements({
			currentYear: session.getPeriod().getYear(),
			allYears: session.getCompany().getYears(),
			allBudgets: await my.budgetRepository.get(),
			allForecasts:
				await my.businessTrendDocumentService.getPublishedForecasts({
					isForecastingEnabled,
				}),
		});

		my.setSecondaryComparisonData();

		if (my.comparisonStateIsValid()) {
			await my.updateComparisonData();
		} else {
			that.showEmpty();
			my.hideActions();
		}
	};

	my.updateComparisonData = async function () {
		await Promise.all([
			my.primaryComparisonData.fetchAnnualTrend(),
			my.secondaryComparisonData.fetchAnnualTrend(),
		]);

		my.businessTrend.setComparisonData(
			my.primaryComparisonData,
			my.secondaryComparisonData,
		);

		my.showActions();
		that.showContent();
	};

	my.renderEmptyContentOn = function (html) {
		html.div(
			{ klass: "alert alert-warning" },
			_("There is no fiscal data, budget, or forecast to compare."),
		);
	};

	my.renderContentOn = function (html) {
		html.render(my.businessTrend);
	};

	my.comparisonDataNameRenderer = function (comparisonData) {
		return comparisonData.getNameRenderer({ router: my.router });
	};

	my.fetchAnnualTrendForFiscalYear = function (fiscalYearId) {
		return my.fiscalYearAnnualTrendRepository.get({
			filter: { fiscalYearId },
		});
	};

	my.fetchAnnualTrendForBudget = function (budgetId) {
		return my.budgetAnnualTrendRepository.get({
			filter: { budgetId },
		});
	};

	my.comparisonStateIsValid = function () {
		return my.primaryComparisonData && my.secondaryComparisonData;
	};

	my.setPrimaryComparisonData = function () {
		const currentPeriod = session.getPeriod();
		const currentYear = currentPeriod.getYear();

		const comparisonElement = new FiscalYearBusinessTrendComparisonElement({
			fiscalYear: currentYear,
		});

		my.primaryComparisonData = my.createComparisonDataObject({
			comparisonElement,
			period: currentPeriod,
		});
	};

	my.setSecondaryComparisonData = function () {
		const comparisonElement =
			my.businessTrendActionsWidget.getComparedElement();

		if (!comparisonElement) {
			my.secondaryComparisonData = undefined;
			return;
		}

		my.secondaryComparisonData = my.createComparisonDataObject({
			comparisonElement,
			period: comparisonElement.findPeriodMatching(
				my.primaryComparisonData.getPeriod(),
			),
		});
	};

	my.createComparisonDataObject = function ({ comparisonElement, period }) {
		return new BusinessTrendComparisonData({
			comparisonElement,
			period,
			annualTrendFetcher: {
				fetchAnnualTrendForFiscalYear: my.fetchAnnualTrendForFiscalYear,
				fetchAnnualTrendForBudget: my.fetchAnnualTrendForBudget,
				fetchAnnualTrendForForecast: (forecast) =>
					my.businessTrendDocumentService.fetchAnnualTrendForForecast(
						forecast,
					),
			},
		});
	};
});

export default businessTrendDocument;
